<template>
  <div>
    <h4 class="text-primary mb-1">Edit Message</h4>
    <div
      v-if="getMessageToEdit?.messageCenterMessageLabel"
      class="text-caption text-medium-emphasis mb-4"
    >
      Editing:
      <span class="font-weight-medium">{{
        getMessageToEdit.messageCenterMessageLabel
      }}</span>
    </div>

    <v-text-field
      label="Message Title"
      color="primary"
      variant="outlined"
      density="compact"
      v-model="messageTitle"
    />

    <v-textarea
      color="primary"
      variant="outlined"
      density="compact"
      name="message"
      label="Message to the Users"
      v-model="messageBody"
    />

    <v-select
      color="primary"
      v-model="selectedType"
      :items="messagesTypesList"
      item-title="messageCenterMessageTypeName"
      item-value="messageCenterMessageTypeId"
      label="Message Type"
      variant="outlined"
      density="compact"
      chips
      clearable
      hint="Select the message type"
      persistent-hint
    >
      <template v-slot:chip="{ internalItem }">
        <v-chip closable color="primary">
          <span class="text-primary">{{ internalItem.title }}</span>
        </v-chip>
      </template>
    </v-select>

    <!-- Image messages carry one of the curated library images -->
    <v-expand-transition>
      <div v-if="isImageType" class="mt-6">
        <div class="text-subtitle-2 text-primary mb-2">Message Image</div>
        <v-row density="comfortable">
          <v-col
            v-for="image in MESSAGE_IMAGES"
            :key="image.key"
            cols="6"
            sm="4"
          >
            <v-card
              class="image-picker-card"
              :class="{
                'image-picker-card--selected': selectedImageKey === image.key,
              }"
              flat
              rounded="lg"
              @click="selectedImageKey = image.key"
            >
              <v-img :src="image.src" :aspect-ratio="16 / 9" cover>
                <div
                  v-if="selectedImageKey === image.key"
                  class="image-picker-card__check"
                >
                  <v-icon color="white" size="24">mdi-check-circle</v-icon>
                </div>
              </v-img>
              <div class="text-caption text-center text-truncate py-1">
                {{ image.label }}
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div class="text-caption text-medium-emphasis">
          The selected image is shown with the message
        </div>
      </div>
    </v-expand-transition>

    <v-autocomplete
      class="mt-6"
      color="primary"
      v-model="selectedUsers"
      :items="usersList"
      item-title="fullName"
      item-value="userId"
      label="Users"
      multiple
      variant="outlined"
      density="compact"
      chips
      clearable
      hint="Select users who will receive the message"
      persistent-hint
      return-object
      @click:clear="clearSelectedUsers"
    >
      <template v-slot:chip="{ internalItem, index }">
        <v-chip
          v-if="index <= 2"
          closable
          @click:close="removeUser(internalItem)"
          color="primary"
        >
          <span class="text-primary">{{ internalItem.title }}</span>
        </v-chip>
        <span
          v-if="index === selectedUsers.length - 1 && index > 2"
          class="text-grey text-caption"
        >
          (+{{ selectedUsers.length - 1 }} others)
        </span>
      </template>
      <template v-slot:append>
        <v-btn
          :disabled="selectedUsers.length > 0 || !usersList.length"
          size="small"
          color="primary"
          class="rounded-2"
          variant="flat"
          :loading="!usersList.length"
          @click="selectAllUsers"
        >
          <div
            class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
          >
            {{
              filteredItems.length
                ? `Select ${filteredItems.length} Users`
                : 'Select All Users'
            }}
          </div>
        </v-btn>
      </template>
    </v-autocomplete>

    <!-- Compact expiration date: opens the calendar in a popover -->
    <v-menu
      v-model="dateMenu"
      :close-on-content-click="false"
      location="bottom start"
    >
      <template v-slot:activator="{ props: activatorProps }">
        <v-text-field
          v-bind="activatorProps"
          class="mt-6"
          :model-value="formattedExpirationDate"
          label="Expiration Date"
          color="primary"
          variant="outlined"
          density="compact"
          prepend-inner-icon="mdi-calendar"
          readonly
          hint="Date the message will expire"
          persistent-hint
        />
      </template>
      <v-date-picker
        v-model="selectedDate"
        :show-current="currentDate"
        color="primary"
        show-adjacent-months
        @update:model-value="dateMenu = false"
      >
        <template v-slot:title>
          <span class="text-subtitle-1">Select Date to Expire the Message</span>
        </template>
        <template v-slot:header="{ header }">
          <div class="px-6 pb-3 pt-2">
            <span class="text-h6">{{
              `${header} ${getCurrentYearAsString()}`
            }}</span>
          </div>
        </template>
      </v-date-picker>
    </v-menu>

    <div class="d-flex ga-3 mt-6" :class="{ 'flex-column': smAndDown }">
      <v-btn
        :loading="isLoading"
        :disabled="isLoading || !isFormValid"
        :block="smAndDown"
        color="primary"
        class="rounded-2"
        variant="flat"
        @click="editMessage"
      >
        <div
          class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
        >
          Save Changes
        </div>
      </v-btn>
      <v-btn
        :disabled="!canPreview"
        :block="smAndDown"
        class="rounded-2"
        color="primary"
        variant="outlined"
        @click="previewDialog = true"
      >
        <div
          class="text-subtitle-2 text-primary text-truncate font-weight-bold px-3"
        >
          Preview
        </div>
      </v-btn>
      <v-btn
        :loading="isLoading"
        :disabled="isLoading"
        :block="smAndDown"
        class="rounded-2"
        color="warning"
        variant="flat"
        @click="cancel"
      >
        <div
          class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
        >
          Cancel
        </div>
      </v-btn>
    </div>

    <!-- Renders the message through the real popup component, straight from
         the unsaved form state — no user assignment or save needed. -->
    <v-dialog v-model="previewDialog" width="auto">
      <PopUpMessageTypeImage
        :slides="[previewSlide]"
        preview
        @close:dialog="previewDialog = false"
      />
    </v-dialog>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { dateTimeToUSFormatFactory } from '@/Core.Patterns/Factory';
import API from '@/Core.Service.Domain/MessageCenter/Management/API';
import {
  MESSAGE_IMAGES,
  MESSAGE_TYPE_ID_IMAGE,
  parseMessageImage,
  serializeMessageImage,
} from '@/Core.Service.Domain/MessageCenter/MessageCenter.Common/utils/messageImage';
import PopUpMessageTypeImage from '@/Core.Service.Domain/MessageCenter/Users/Components/PopUpMessageTypeImage.vue';

import { ISlide } from '@/Core.Service.Domain/MessageCenter/Users/Components/types/Slide.ts';

// Responsive helpers
const { smAndDown } = useDisplay();

// Store
const store = useStore();
const usersList = computed(
  () => store.getters['MessageCenterManagementStore/getUsersList']
);
const messagesTypesList = computed(
  () => store.getters['MessageCenterManagementStore/getMessagesTypesList']
);
const getMessageToEdit = computed(
  () => store.getters['MessageCenterManagementStore/getMessageToEdit']
);

// Reactive variables<Refs>
const currentDate = ref(new Date());
const selectedDate = ref(new Date());
const dateMenu = ref(false);
const isLoading = ref(false);
const selectedUsers = ref<number[] | { userId: number; fullName: '' }[]>([]);
const selectedUsersTemp = ref<number[] | { userId: number; fullName: '' }[]>(
  []
);
const filteredItems = ref([]);
const messageBody = ref('');
const messageTitle = ref('');
const selectedType = ref(null);
const selectedImageKey = ref<string | null>(MESSAGE_IMAGES[0]?.key ?? null);
const previewDialog = ref(false);

// Dirty tracking: snapshot the editable fields on load and compare against it.
// User IDs are tracked separately because removals persist immediately via the
// disassociate API (pristineUserIds is rebased after those calls), while
// additions only persist on Save and must count as unsaved changes.
const pristineSnapshot = ref('');
const pristineUserIds = ref<number[]>([]);
const buildSnapshot = () =>
  JSON.stringify({
    messageTitle: messageTitle.value || '',
    messageBody: messageBody.value || '',
    selectedType: selectedType.value,
    selectedImageKey: selectedImageKey.value,
    selectedDate: selectedDate.value
      ? new Date(selectedDate.value).getTime()
      : null,
  });

const sortedSelectedUserIds = () =>
  selectedUsers.value
    .map((user: number | { userId: number }) =>
      typeof user === 'object' && user !== null ? user.userId : user
    )
    .sort((a: number, b: number) => a - b);

// Computed
const isDirty = computed(
  () =>
    buildSnapshot() !== pristineSnapshot.value ||
    JSON.stringify(sortedSelectedUserIds()) !==
      JSON.stringify(pristineUserIds.value)
);

const isImageType = computed(
  () => selectedType.value === MESSAGE_TYPE_ID_IMAGE
);

const canPreview = computed(
  () =>
    (messageTitle.value || '').length > 0 &&
    (messageBody.value || '').length > 0 &&
    selectedType.value !== null
);

const previewSlide = computed<ISlide>(() => ({
  messagingCenterMessageValue: isImageType.value
    ? serializeMessageImage(messageBody.value, selectedImageKey.value)
    : messageBody.value,
  messageCenterMessageLabel: messageTitle.value,
  messageCenterMessageTypeId: selectedType.value ?? 0,
  userMessageCenterMessageId: 0,
  messageCenterMessageId: 0,
}));

const isFormValid = computed(() => {
  return (
    (messageBody.value || '').length > 0 &&
    (messageTitle.value || '').length > 0 &&
    selectedType.value !== null &&
    selectedDate.value !== null &&
    selectedDate.value !== undefined
  );
});

const formattedExpirationDate = computed(() =>
  selectedDate.value
    ? dateTimeToUSFormatFactory(selectedDate.value, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''
);

// Lifecycle Hooks
onMounted(() => {
  setFormValues();
});

onBeforeUnmount(() => {
  store.dispatch('MessageCenterManagementStore/setEditMessageDirty', false);
});

watch(
  () => selectedUsers.value,
  (newValue) => {
    if (newValue.length > 0) {
      selectedUsersTemp.value = newValue;
    }
  }
);

// Reload the form when switching to another message: the route param changes
// but Vue Router reuses this component instance, so onMounted won't fire again.
watch(getMessageToEdit, () => {
  setFormValues();
});

// Let the sent-messages list know whether this form has unsaved edits.
watch(isDirty, (dirty) => {
  store.dispatch('MessageCenterManagementStore/setEditMessageDirty', dirty);
});

// Store actions
const getAllMessagesFromMessageCenter = () => {
  store.dispatch(
    'MessageCenterManagementStore/getAllMessagesFromMessageCenter'
  );
};

const setUnreadMessagesCount = () => {
  store.dispatch('MessageCenterUsersStore/setUnreadMessagesCount');
};

const setMessagesByUser = () => {
  store.dispatch('MessageCenterUsersStore/setMessagesByUser');
};

// Methods
const setFormValues = () => {
  const { imageKey, cleanValue } = parseMessageImage(
    getMessageToEdit.value.messagingCenterMessageValue
  );
  messageBody.value = cleanValue;
  selectedImageKey.value = imageKey ?? MESSAGE_IMAGES[0]?.key ?? null;
  messageTitle.value = getMessageToEdit.value.messageCenterMessageLabel ?? '';
  selectedUsers.value = (getMessageToEdit.value.usersId ?? []).map(
    (user: number) => {
      return user;
    }
  );
  selectedType.value = getMessageToEdit.value.messageCenterMessageTypeId;
  selectedDate.value = new Date(
    getMessageToEdit.value.messageCenterMessageExpirationDateTimeOffset
  );
  pristineSnapshot.value = buildSnapshot();
  pristineUserIds.value = sortedSelectedUserIds();
};

const route = useRoute();
const removeUser = async (item: { value: number }) => {
  isLoading.value = true;
  const { id } = route.params;
  const index = selectedUsers.value.findIndex((user) => user === item.value);
  if (index > -1) {
    selectedUsers.value.splice(index, 1);
  }
  if (filteredItems.value.length) {
    filteredItems.value = filteredItems.value.filter(
      (user: { userId: number }) => user.userId !== item.value
    );
  }
  try {
    await API.disassociateUserToMessage({
      MessageCenterMessageId: Number(id),
      UserIds: [item.value],
    });
    // Removal is persisted, so it no longer counts as an unsaved change.
    pristineUserIds.value = pristineUserIds.value.filter(
      (userId) => userId !== item.value
    );
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};

const clearSelectedUsers = async () => {
  isLoading.value = true;
  const { id } = route.params;
  try {
    await API.disassociateUserToMessage({
      MessageCenterMessageId: Number(id),
      UserIds: selectedUsersTemp.value.map((user) => user as number),
    });
    selectedUsersTemp.value = [];
    filteredItems.value = [];
    // The clear is persisted, so it no longer counts as an unsaved change.
    pristineUserIds.value = [];
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};

const selectAllUsers = () => {
  if (filteredItems.value.length) {
    selectedUsers.value = filteredItems.value.map(
      (user: { userId: number }) => user.userId
    );
  } else {
    selectedUsers.value = usersList.value.map(
      (item: { userId: number }) => item.userId
    );
  }
};

const router = useRouter();
const editMessage = async () => {
  isLoading.value = true;
  const { id } = route.params;
  try {
    await API.messageCenterEditMessage({
      MessageCenterMessageId: Number(id),
      MessageCenterMessageLabel: messageTitle.value,
      MessageCenterMessageValue: isImageType.value
        ? serializeMessageImage(messageBody.value, selectedImageKey.value)
        : messageBody.value,
      MessageCenterMessageTypeId:
        selectedType.value !== null ? selectedType.value : '',
      MessageCenterMessageExpirationDateTimeOffset: convertDateToUSFormat(
        selectedDate.value
      ),
    });

    if (selectedUsers.value.length) {
      await API.associateUserToMessage({
        MessageCenterMessageId: Number(id),
        UserIds: getUserIdsFromSelectedUsers() as number[],
      });
    }

    getAllMessagesFromMessageCenter();
    setUnreadMessagesCount();
    // The popup builds its slides from the user store — refresh it here since
    // we no longer leave the route (the route watcher used to do this).
    setMessagesByUser();

    // Stay on the edit route: reload the saved message so the form (and its
    // pristine snapshot, via the getMessageToEdit watcher) rebase on what the
    // server actually stored.
    await store.dispatch(
      'MessageCenterManagementStore/editMessageFromMessageCenter',
      Number(id)
    );
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};

const getUserIdsFromSelectedUsers = () => {
  return selectedUsers.value.map((user) => {
    if (typeof user === 'object' && 'userId' in user) {
      return user.userId;
    } else {
      return user;
    }
  });
};
const cancel = () => {
  router.push({
    name: 'MessageCenter',
  });
};

const convertDateToUSFormat = (date: Date) => {
  const newDate = new Date(date);
  return `${newDate.getFullYear()}-${
    newDate.getMonth() + 1
  }-${newDate.getDate()}`;
};

const getCurrentYearAsString = (): string => {
  return new Date().getFullYear().toString();
};
</script>
<style scoped lang="scss">
.v-date-picker ::v-deep(.v-date-picker__title) {
  text-transform: none;
}

.v-picker ::v-deep(.v-picker-title) {
  padding-top: 14px;
  padding-bottom: 0;
}

.image-picker-card {
  cursor: pointer;
  border: 2px solid rgba(0, 0, 0, 0.12);
  transition: border-color 0.2s ease;

  &--selected {
    border-color: rgb(var(--v-theme-primary));
  }

  &__check {
    position: absolute;
    top: 6px;
    right: 6px;
    padding: 2px;
    line-height: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
  }
}
</style>
